import {
  Mic,
  PhoneCall,
  PhoneOff,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import AudioWaveform from "@/components/AudioWaveform";
import type { Company } from "@/services/companyApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type PlaygroundPanelProps = {
  micActive: boolean;
  workletsLoaded: boolean;
  handleMicToggle: () => void;
  selectedAudioInput: string;
  setSelectedAudioInput: (input: string) => void;
  audioInputDevices: MediaDeviceInfo[];
  analyserNodeRef: React.RefObject<AnalyserNode>;
  wsConnected: boolean;
  onCompanyChange: (config: any) => void;
  handleGoToCompany: (companyId: string) => void;
  companies: Company[];
  companiesLoading: boolean;
  selectedCompanyId: string | null;
  // Audio device management props
  audioDevicesLoading?: boolean;
  audioDevicesError?: string | null;
  hasAudioPermission?: boolean;
  onRefreshDevices?: () => void;
  onRequestPermission?: () => void;
};
export default function PlaygroundPanel({
  micActive,
  workletsLoaded,
  handleMicToggle,
  selectedAudioInput,
  setSelectedAudioInput,
  audioInputDevices,
  analyserNodeRef,
  wsConnected,
  onCompanyChange,
  handleGoToCompany,
  companies,
  companiesLoading,
  selectedCompanyId,
  audioDevicesLoading = false,
  audioDevicesError = null,
  hasAudioPermission = false,
  onRefreshDevices,
  onRequestPermission,
}: PlaygroundPanelProps) {
  const handleCompanyChange = (value: string) => {
    onCompanyChange({ selectedCompanyId: value });
  };

  const labelsEmpty =
    audioInputDevices.length > 0 &&
    audioInputDevices.every((d) => !d.label || d.label.trim().length === 0);
  const shouldShowRefresh =
    Boolean(onRefreshDevices) &&
    (!!audioDevicesError ||
      !hasAudioPermission ||
      audioInputDevices.length === 0 ||
      labelsEmpty);

  return (
    <div className="p-4 border  rounded-lg flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 pl-1 mb-1">
            Company
          </label>
          <div className="flex flex-row items-center gap-2 ">
            <Select
              value={selectedCompanyId ?? ""}
              onValueChange={handleCompanyChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    companiesLoading ? "Loading companies..." : "Select company"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCompanyId && (
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <Button
                  variant="outline"
                  onClick={() => handleGoToCompany(selectedCompanyId)}
                >
                  View company
                </Button>
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label
              htmlFor="mic-select"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 pl-1"
            >
              Input Device
            </label>
            {shouldShowRefresh && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefreshDevices}
                    disabled={audioDevicesLoading || micActive}
                    className="h-6 w-6 p-0"
                    aria-label="Refresh input devices"
                    title="Refresh input devices"
                  >
                    <RefreshCw
                      className={`h-3 w-3 ${
                        audioDevicesLoading ? "animate-spin" : ""
                      }`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Most changes auto-detected</TooltipContent>
              </Tooltip>
            )}
          </div>

          {audioDevicesError && (
            <Alert className="mb-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {audioDevicesError}
                {!hasAudioPermission && onRequestPermission && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={onRequestPermission}
                    className="h-auto p-0 ml-2 text-xs underline"
                  >
                    Grant permission
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Select
            value={selectedAudioInput}
            onValueChange={setSelectedAudioInput}
          >
            <SelectTrigger
              id="mic-select"
              className="mt-1 w-full"
              disabled={micActive || audioDevicesLoading}
            >
              <SelectValue
                placeholder={
                  audioDevicesLoading
                    ? "Loading devices..."
                    : audioInputDevices.length === 0
                    ? "No devices found"
                    : "Select input device"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {audioInputDevices.map((device, index) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${index + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <button
            onClick={handleMicToggle}
            disabled={!workletsLoaded}
            className={`px-4 py-2 rounded cursor-pointer ${
              !workletsLoaded
                ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                : micActive
                ? "bg-red-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            <span className="flex flex-row items-center">
              <Mic className="w-4 h-4 mr-2" />
              {!workletsLoaded
                ? "Loading Worklets..."
                : micActive
                ? "Stop Mic"
                : "Start Mic"}
            </span>
          </button>
          <div className="flex-grow max-w-[200px]">
            <AudioWaveform
              analyser={analyserNodeRef.current}
              isActive={micActive}
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {wsConnected ? (
            <div className="text-green-500 flex items-center gap-2">
              <PhoneCall className="w-4 h-4" />
              Connected
            </div>
          ) : (
            <div className="text-red-500 flex items-center gap-2">
              <PhoneOff className="w-4 h-4" />
              Disconnected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
